import axios, { AxiosError } from 'axios';
import { logger } from 'src/lib/logger';
import { API_URL, COOKIE_NAME } from 'src/env';
import { authStorage } from 'src/lib/auth/authStorage';
import { injectTraceContext } from 'src/lib/tracing';
import { authEvents } from 'src/lib/auth/authEvents';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const getSessionToken = (): string | null => {
  const sessionCookie = document.cookie.split(';').find((cookie) => cookie.trim().startsWith(`${COOKIE_NAME}=`));
  return sessionCookie ? sessionCookie.split('=')[1] : localStorage.getItem(COOKIE_NAME);
};

export const setSessionToken = (token: string): void => {
  localStorage.setItem(COOKIE_NAME, token);
};

export const clearSessionToken = (): void => {
  localStorage.removeItem(COOKIE_NAME);
  authStorage.clearRefreshToken();
};

const PROACTIVE_REFRESH_MS = 50 * 60 * 1000;
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

export const scheduleProactiveRefresh = (): void => {
  clearProactiveRefresh();
  proactiveRefreshTimer = setTimeout(async () => {
    logger.info('[api] Proactive session refresh (before expiry)');
    const success = await refreshSession();
    if (success) {
      scheduleProactiveRefresh();
    }
  }, PROACTIVE_REFRESH_MS);
};

export const clearProactiveRefresh = (): void => {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
};

let isRefreshing = false;
let refreshQueue: Array<{ resolve: (success: boolean) => void; timeoutId: ReturnType<typeof setTimeout> }> = [];

const REFRESH_QUEUE_TIMEOUT_MS = 30000;

const processRefreshQueue = (success: boolean): void => {
  refreshQueue.forEach((item) => {
    clearTimeout(item.timeoutId);
    item.resolve(success);
  });
  refreshQueue = [];
};

interface RefreshSessionResponse {
  message: string;
  session_token?: string;
  refresh_token?: string;
}

export const refreshSession = async (): Promise<boolean> => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        const index = refreshQueue.findIndex((item) => item.timeoutId === timeoutId);
        if (index !== -1) {
          refreshQueue.splice(index, 1);
        }
        logger.warn('[api] Refresh queue timeout - resolving with failure');
        resolve(false);
      }, REFRESH_QUEUE_TIMEOUT_MS);

      refreshQueue.push({ resolve, timeoutId });
    });
  }

  isRefreshing = true;
  logger.info('[api] Starting session refresh');

  try {
    let requestBody: { refresh_token?: string } | undefined;
    const refreshToken = authStorage.loadRefreshToken();
    if (refreshToken) {
      requestBody = { refresh_token: refreshToken };
      logger.debug('[api] Sending refresh token in request body');
    }

    const response = await api.put<RefreshSessionResponse>('/v1/sessions', requestBody);

    if (response.data.session_token) {
      setSessionToken(response.data.session_token);
    }
    if (response.data.refresh_token) {
      authStorage.saveRefreshToken(response.data.refresh_token);
    }

    logger.info('[api] Session refreshed successfully');
    processRefreshQueue(true);
    authEvents.emit('tokenRefreshed');
    return true;
  } catch (error) {
    logger.warn('[api] Session refresh failed:', error);
    processRefreshQueue(false);
    return false;
  } finally {
    isRefreshing = false;
  }
};

export const setupAuthInterceptors = (onAuthFailure: () => void) => {
  api.interceptors.request.clear();
  api.interceptors.response.clear();

  api.interceptors.request.use((config) => {
    const traceContext = injectTraceContext();
    if (traceContext.traceparent && config.headers) {
      config.headers['traceparent'] = traceContext.traceparent;
      if (traceContext.tracestate) {
        config.headers['tracestate'] = traceContext.tracestate;
      }
    }

    const token = getSessionToken();

    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      const axiosError = error instanceof Error ? error : new Error(String(error));

      if (error.response?.status === 401) {
        if (originalRequest.url === '/v1/sessions' && originalRequest.method === 'put') {
          clearSessionToken();
          onAuthFailure();
          throw axiosError;
        }

        if (originalRequest._retry) {
          clearSessionToken();
          onAuthFailure();
          throw axiosError;
        }

        originalRequest._retry = true;

        const refreshed = await refreshSession();

        if (refreshed) {
          return api(originalRequest);
        } else {
          clearSessionToken();
          onAuthFailure();
          throw axiosError;
        }
      }

      throw axiosError;
    },
  );
};

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError && error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error instanceof AxiosError && error.response?.data?.message) {
    return error.response.data.message;
  }
  return 'An error occurred';
};

export const isPermissionError = (error: unknown): boolean =>
  error instanceof AxiosError && error.response?.status === 403;

export const shouldRetryQuery = (failureCount: number, error: Error): boolean => {
  const axiosError = error as AxiosError;

  if (axiosError?.response?.status === 401) {
    return false;
  }

  if (!axiosError?.response) {
    return false;
  }

  if (axiosError?.response?.status >= 400 && axiosError?.response?.status < 500) {
    return false;
  }

  if (axiosError?.response?.status >= 500) {
    return failureCount < 2;
  }

  return false;
};

export default api;
